import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useI18n, formatPrice } from "../i18n";
import { api } from "../api";
import { Crown, Award, Sparkles, ArrowRight, CheckCircle2 } from "lucide-react";

const TIER_ICON = {
  bronze: Award,
  silver: Sparkles,
  gold: Crown,
};
const TIER_ACCENT = {
  bronze: "border-amber-700/60 from-amber-900/20",
  silver: "border-zinc-400/60 from-zinc-400/10",
  gold: "border-[#FFB400] from-[#FFB400]/20",
};

const Loyalty = () => {
  const { lang } = useI18n();
  const isPt = lang === "pt";
  const [tiers, setTiers] = useState([]);
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/loyalty/tiers").then((r) => setTiers(r.data)).catch(() => setTiers([]));
  }, []);

  const onLookup = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.post("/loyalty/lookup", { email });
      setResult(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20" data-testid="loyalty-page">
      <p className="text-label text-[#FFB400]">
        <span className="line-accent" />
        Movix Club
      </p>
      <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-bold tracking-tighter text-white max-w-3xl">
        {isPt
          ? "Quanto mais você roda, mais a Movix devolve."
          : "The more you drive with us, the more you get back."}
      </h1>
      <p className="mt-6 max-w-2xl text-zinc-300 text-lg leading-relaxed">
        {isPt
          ? "Cada locação paga conta automaticamente. Sem cartão, sem app, sem ponto que vence. É só rodar."
          : "Every paid rental counts automatically. No card, no app, no expiring points. Just drive."}
      </p>

      {/* Tier cards */}
      <div
        className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6"
        data-testid="loyalty-tiers"
      >
        {tiers.map((t) => {
          const Icon = TIER_ICON[t.id] || Award;
          return (
            <div
              key={t.id}
              className={`bg-gradient-to-b ${
                TIER_ACCENT[t.id] || ""
              } to-transparent bg-[#0d0d0d] border ${
                t.id === "gold" ? "border-[#FFB400]" : "border-white/10"
              } p-8`}
              data-testid={`tier-${t.id}`}
            >
              <div className="flex items-center justify-between">
                <Icon size={28} className={t.id === "gold" ? "text-[#FFB400]" : "text-white"} />
                <p className="text-label">
                  {t.min_rentals}+ {isPt ? "locações" : "rentals"}
                </p>
              </div>
              <h3 className="mt-4 font-heading text-3xl font-bold text-white tracking-tight">
                {t.label}
              </h3>
              {t.discount_pct > 0 && (
                <p className="mt-2 text-[#FFB400] font-mono-ui text-sm uppercase tracking-widest">
                  {t.discount_pct}% {isPt ? "off em toda locação" : "off every rental"}
                </p>
              )}
              <ul className="mt-6 space-y-3 text-sm">
                {(isPt ? t.perks_pt : t.perks_en).map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-zinc-300">
                    <CheckCircle2 size={16} className="text-[#FFB400] mt-0.5 flex-shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      {/* Status checker */}
      <div className="mt-20 bg-[#0d0d0d] border border-white/10 p-8 md:p-10" data-testid="loyalty-checker">
        <p className="text-label text-[#FFB400]">
          {isPt ? "Veja seu nível" : "Check your tier"}
        </p>
        <h2 className="mt-3 font-heading text-3xl font-semibold text-white tracking-tight">
          {isPt ? "Qual é o seu Movix Club agora?" : "Where are you on the ladder?"}
        </h2>
        <form onSubmit={onLookup} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-xl">
          <input
            type="email"
            placeholder={isPt ? "Seu e-mail" : "Your email"}
            className="movix-input flex-1"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="loyalty-email"
          />
          <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60" data-testid="loyalty-submit">
            {loading ? "…" : isPt ? "Conferir" : "Check"} <ArrowRight size={14} />
          </button>
        </form>

        {result && (
          <div className="mt-8 bg-[#080808] border border-[#FFB400]/40 p-6" data-testid="loyalty-result">
            <p className="text-label text-[#FFB400]">{isPt ? "Você é" : "You are"}</p>
            <p className="font-heading text-4xl font-bold text-white mt-1">{result.tier.label}</p>
            <p className="text-zinc-400 text-sm mt-2">
              {isPt ? "Locações pagas" : "Paid rentals"}: <strong className="text-white">{result.paid_rentals}</strong>
              {" · "}
              {isPt ? "Total incluindo solicitações" : "Total incl. requests"}:{" "}
              <strong className="text-white">{result.total_reservations}</strong>
            </p>
            {result.next_tier && (
              <p className="mt-4 text-sm text-zinc-300">
                {isPt
                  ? `Faltam ${result.to_next} locações para chegar em ${result.next_tier.label}.`
                  : `${result.to_next} more rentals to reach ${result.next_tier.label}.`}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mt-12 text-center" data-testid="loyalty-cta-fleet">
        <p className="text-zinc-400">
          {isPt
            ? "Não tem nenhuma locação ainda? Comece pelo Versa a $28.99/dia."
            : "No rentals yet? Start with the Versa at $28.99/day."}
        </p>
        <a href="/fleet" className="btn-primary inline-flex mt-4">
          {isPt ? "Ver a frota" : "See the fleet"} <ArrowRight size={14} />
        </a>
        <p className="mt-4 text-xs text-zinc-600 font-mono-ui uppercase tracking-widest">
          Movix Club · {formatPrice(0)} {isPt ? "para entrar" : "to join"}
        </p>
      </div>
    </div>
  );
};

export default Loyalty;
